import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { parseUtc } from '../utils/time';

const fmt = (n) => (n == null ? '—' : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`);
const fmtBig = (n) => (n == null ? '—'
  : n >= 1e6 ? `${(n / 1e6).toFixed(2)}M`
  : n >= 1e3 ? `${(n / 1e3).toFixed(1)}k` : `${n}`);

const GREEN = '#4ade80';

// Heartbeats land ~every 5s, so the sample buffer spans samples*5s of history
const SECONDS_PER_SAMPLE = 5;
const MAX_SAMPLES = 60;

const OPERATORS = [
  { key: 'letsencrypt', label: "Let's Encrypt", color: '#4ade80' },
  { key: 'digicert',    label: 'DigiCert',      color: '#60a5fa' },
  { key: 'sectigo',     label: 'Sectigo',       color: '#a78bfa' },
  { key: 'cloudflare',  label: 'Cloudflare',    color: '#fb923c' },
  { key: 'trustasia',   label: 'TrustAsia',     color: '#f472b6' },
  { key: 'geomys',      label: 'Geomys',        color: '#facc15' },
  { key: 'ipng',        label: 'IPng',          color: '#2dd4bf' },
  { key: 'other',       label: 'Other',         color: '#6b7280' },
];

/* Live certs/sec sparkline built from heartbeat samples (appended only when a
   new heartbeat arrives, so the line reflects real samples, not poll repeats). */
function Sparkline({ data, color, height = 44 }) {
  const W = 100, H = height;
  if (data.length < 2) {
    return <Box sx={{ height: H, display: 'flex', alignItems: 'center' }}>
      <Typography sx={{ fontSize: '0.66rem', color: 'rgba(232,228,248,0.3)' }}>
        Sampling…
      </Typography>
    </Box>;
  }
  const max = Math.max(...data, 1);
  const pts = data.map((v, i) => [(i / (data.length - 1)) * W, H - (v / max) * (H - 4) - 2]);
  const line = pts.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
  const area = `0,${H} ${line} ${W},${H}`;
  return (
    <Box sx={{ position: 'relative' }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
        style={{ width: '100%', height: H, display: 'block' }}>
        <defs>
          <linearGradient id="spark" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.28" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points={area} fill="url(#spark)" />
        <polyline points={line} fill="none" stroke={color} strokeWidth="1.5"
          strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="2" fill={color} />
      </svg>
    </Box>
  );
}

function Metric({ label, value, color }) {
  return (
    <Box>
      <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, letterSpacing: 0.5,
                        textTransform: 'uppercase', color: 'rgba(232,228,248,0.4)' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: color || '#e8e4f8',
                        lineHeight: 1.2, letterSpacing: -0.3,
                        fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
    </Box>
  );
}

/* Per-operator share of the current stream window: stacked bar + legend */
function OperatorShare({ byOperator }) {
  const counts = OPERATORS
    .map(op => ({ ...op, count: (byOperator || {})[op.key] || 0 }))
    .filter(op => op.count > 0);
  const total = counts.reduce((a, b) => a + b.count, 0);
  if (!total) return null;
  return (
    <Box sx={{ mt: 1.2 }}>
      <Box sx={{ display: 'flex', height: 5, borderRadius: 3, overflow: 'hidden',
                 bgcolor: 'rgba(255,255,255,0.05)' }}>
        {counts.map(op => (
          <Tooltip key={op.key} title={`${op.label}: ${op.count.toLocaleString()} certs (${Math.round((op.count / total) * 100)}%)`}
            placement="top">
            <Box sx={{ width: `${(op.count / total) * 100}%`, bgcolor: op.color, opacity: 0.85 }} />
          </Tooltip>
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 1.4, mt: 0.6, flexWrap: 'wrap' }}>
        {counts.map(op => (
          <Box key={op.key} display="flex" alignItems="center" gap={0.4}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: op.color }} />
            <Typography sx={{ fontSize: '0.6rem', color: 'rgba(232,228,248,0.45)' }}>
              {op.label} <b style={{ color: 'rgba(232,228,248,0.7)' }}>
                {Math.round((op.count / total) * 100)}%</b>
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/* Daily log-list drift check (scheduler, persisted in system_status). Shown
   here because the report used to live only in a container log, where a
   months-long coverage gap went unnoticed. Three states: ok, warn, unknown. */
function LogCoverage({ loglist }) {
  if (!loglist) return null;
  const { status, watched, dead = [], new_operators: ops = [], pending_operators: pend = [],
          expiring = [], live_usable_non_google: usable, checked_at: at, error } = loglist;
  const warn = status === 'warn';
  const unknown = status !== 'ok' && !warn;
  const color = warn ? '#fbbf24' : unknown ? 'rgba(232,228,248,0.3)' : GREEN;
  let value;
  if (unknown) value = error ? 'Check Failed' : 'Awaiting First Check';
  else if (dead.length) value = `${dead.length} Dead Log${dead.length > 1 ? 's' : ''}`;
  else if (ops.length) value = `Not Watched: ${ops.join(', ')}`;
  else value = usable ? `All ${usable} Usable Logs Watched` : `${watched} Logs Watched`;
  const notes = [];
  if (pend.length) notes.push(`Approved but not yet in service: ${pend.join(', ')}`);
  if (expiring.length) notes.push(`${expiring.length} watched shard${expiring.length > 1 ? 's' : ''} expire within the warning window; successors are already watched`);
  if (at) notes.push(`Checked ${parseUtc(at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}`);
  return (
    <Tooltip title={`Chrome CT log list compared with the logs this stream follows. ${notes.join('. ')}`}
      placement="top">
      <Box display="flex" alignItems="center" gap={0.5} mt={0.9}>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
        <Typography sx={{ fontSize: '0.6rem', color: 'rgba(232,228,248,0.45)' }}>
          Log Coverage <b style={{ color: warn ? color : 'rgba(232,228,248,0.7)' }}>{value}</b>
        </Typography>
      </Box>
    </Tooltip>
  );
}

/** Real-time CT stream throughput: live certs/sec + sparkline + running totals. */
function StreamMonitor({ stream, loglist }) {
  const [history, setHistory] = useState([]);
  const [peak, setPeak] = useState(0);
  const lastSeen = useRef(null);
  const prevProcessed = useRef(null);

  useEffect(() => {
    if (!stream) return;
    // The running total dropped: the counters were zeroed by a reset. Wipe the
    // client-side peak + sparkline too, so the whole box reads clean and not
    // just the server-side totals.
    const tp = stream.total_processed;
    if (tp != null && prevProcessed.current != null && tp < prevProcessed.current) {
      setHistory([]);
      setPeak(0);
      lastSeen.current = null;
    }
    if (tp != null) prevProcessed.current = tp;

    if (!stream.last_seen || stream.last_seen === lastSeen.current) return;
    lastSeen.current = stream.last_seen;
    const rate = stream.connected ? (stream.certs_per_sec || 0) : 0;
    setHistory(h => [...h, rate].slice(-MAX_SAMPLES));
    setPeak(p => Math.max(p, rate));
  }, [stream]);

  const connected = stream?.connected;
  const rate = connected ? (stream.certs_per_sec || 0) : 0;
  const windowMin = Math.max(1, Math.round((history.length * SECONDS_PER_SAMPLE) / 60));

  return (
    <Box>
      <Box display="flex" alignItems="flex-end" justifyContent="space-between" gap={2} mb={0.5}>
        <Box display="flex" alignItems="baseline" gap={0.8}>
          <Typography sx={{ fontSize: '2.4rem', fontWeight: 800, lineHeight: 1,
                            color: connected ? GREEN : 'rgba(232,228,248,0.4)', letterSpacing: -1.5,
                            fontVariantNumeric: 'tabular-nums' }}>
            {rate}
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: 'rgba(232,228,248,0.45)' }}>
            Certs / Sec
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '0.62rem', color: 'rgba(232,228,248,0.3)',
                          fontVariantNumeric: 'tabular-nums' }}>
          Peak {peak} /s
        </Typography>
      </Box>
      {/* Make clear this is the global CT firehose, not a scan of the user's
          own domains: the number keeps moving because the internet keeps
          issuing certificates, regardless of resets or brand count. */}
      <Typography sx={{ fontSize: '0.56rem', color: 'rgba(232,228,248,0.28)', mb: 0.5 }}>
        Live Internet-Wide Certificate Feed, Not a Scan of Your Domains
      </Typography>

      <Sparkline data={history} color={connected ? GREEN : '#6b7280'} />
      {history.length >= 2 && (
        <Box display="flex" justifyContent="space-between" mt={0.2}>
          <Typography sx={{ fontSize: '0.56rem', color: 'rgba(232,228,248,0.22)' }}>
            {windowMin}m Ago
          </Typography>
          <Typography sx={{ fontSize: '0.56rem', color: 'rgba(232,228,248,0.22)' }}>
            Now
          </Typography>
        </Box>
      )}

      <OperatorShare byOperator={stream?.by_operator} />
      <LogCoverage loglist={loglist} />

      <Box display="flex" gap={3} mt={1.2}>
        <Metric label="Certs watched" value={fmtBig(stream?.total_processed)} />
        <Metric label="Brand matches" value={fmt(stream?.total_matches)}
          color={stream?.total_matches ? '#f87171' : '#e8e4f8'} />
      </Box>
    </Box>
  );
}

export default StreamMonitor;
